import React, { useState , useEffect } from 'react';
import { Link } from "react-router-dom";
import { API } from "aws-amplify";
import swal from '@sweetalert/with-react';

import Button from '../../Button';
import LabelColorPicker from '../common/LabelColorPicker';

import { sortArrayOfObjects, createUniqueObjectID } from '../../../js/Helpers';
import { LABEL_COLOR_OPTIONS, DEFAULT_LABEL_COLOR, getLabelColorClass, isInclusionLabel } from '../utils/labelColorUtils';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faQuestionCircle, faTimes, faCheck } from '@fortawesome/free-solid-svg-icons';

export default function CreateLabels({ isLoading, setIsLoading, subscreenRestricted, subscreenLocked, schoolInfo, schoolType, scheduleInfo, departments, userInfo, coursesLoading, courses, teachersLoading, teachers, sectionsLoading, sections, setSections, subsections, labels, setLabels, handleMoveToNextScreen, setScreenAllowsUniversalSidebar, setUniversalSidebarLoadingInfo }) {
	const [showLabelsDescription, setShowLabelsDescription] = useState(false);
	const [editingLabelID, setEditingLabelID] = useState(null);
	const [editingLabelName, setEditingLabelName] = useState('');
	const [isSavingEditedLabelName, setIsSavingEditedLabelName] = useState(false);
	const [loadingLabelIDs, setLoadingLabelIDs] = useState([]);
	
	const addLabel = () => {
		const newID = createUniqueObjectID(5);

		setEditingLabelID(null);
		setEditingLabelName('');
		
		labels.push({new:true, label_id:newID, label:null, tag_color:DEFAULT_LABEL_COLOR});
		setLabels([...labels]);
	}

	const startEditingLabelName = (label_info) => {
		if(!label_info || !label_info.label_id || !label_info.label) return;

		setEditingLabelID(label_info.label_id);
		setEditingLabelName(label_info.label);
	}

	const cancelEditingLabelName = () => {
		if(isSavingEditedLabelName) return;
		setEditingLabelID(null);
		setEditingLabelName('');
	}

	useEffect(() => {
		if(!editingLabelID || isSavingEditedLabelName) return;

		const handlePointerOutsideLabelEditor = (event) => {
			const target = event.target;
			if(target?.closest?.('.create-labels-editing-label')) return;

			cancelEditingLabelName();
		};

		document.addEventListener('mousedown', handlePointerOutsideLabelEditor);
		document.addEventListener('touchstart', handlePointerOutsideLabelEditor);

		return () => {
			document.removeEventListener('mousedown', handlePointerOutsideLabelEditor);
			document.removeEventListener('touchstart', handlePointerOutsideLabelEditor);
		};
	}, [editingLabelID, isSavingEditedLabelName]);

	const saveEditedLabelName = async (label_id) => {
		if(isSavingEditedLabelName) return;

		const label_index = labels.findIndex(label => `${label.label_id}` === `${label_id}`);
		if(label_index === -1) return;

		const label_info = labels[label_index];
		const previous_label = label_info.label;
		const trimmed_label_name = `${editingLabelName}`.trim();

		if(!trimmed_label_name) return;
		if(trimmed_label_name === previous_label)
		{
			setEditingLabelID(null);
			setEditingLabelName('');
			return;
		}

		labels[label_index].label = trimmed_label_name;
		setLabels([...labels]);
		setIsSavingEditedLabelName(true);

		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, update_type:'update', label_id:label_id, label:trimmed_label_name, tag_color:getLabelColorClass(label_info)};
		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/admin/update-label';
		const myInit = {
			response: true,
			body: JSON.stringify(data),
		};

		try {
			const response = await API.post(apiName, url, myInit);
			const saved_tag_color = (response.data && response.data.tag_color) ? response.data.tag_color : getLabelColorClass(label_info);

			labels[label_index].tag_color = saved_tag_color;
			setLabels([...labels]);
			setEditingLabelID(null);
			setEditingLabelName('');
		} catch(e)
		{
			labels[label_index].label = previous_label;
			setLabels([...labels]);
			console.log(e.response);
		} finally
		{
			setIsSavingEditedLabelName(false);
		}
	}
	
	const updateLabel = async (e, label_id) => {
		const label_index = labels.findIndex(label => label.label_id === label_id);
		const label_info = labels[label_index];
		
		const label_input = e.target.closest('.create-labels-editing-label').querySelector('.create-labels-input');
		const label = label_input.value;
		
		if(!label)
		{
			label_input.classList.add('error');
			return;
		}
		
		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, update_type:'add', label_id:label_id, label:label, tag_color:getLabelColorClass(label_info)};
		
		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/admin/update-label';
	    const myInit = { // OPTIONAL
	        response: true,
	        body: JSON.stringify(data),
	    };
	   
		try {
			setLoadingLabelIDs((previous_ids) => previous_ids.includes(`${label_id}`) ? previous_ids : [...previous_ids, `${label_id}`]);
			const response = await API.post(apiName, url, myInit);
			const new_label_id = response.data.label_id;
			const new_tag_color = (response.data && response.data.tag_color) ? response.data.tag_color : getLabelColorClass(label_info);
			
			labels[label_index]['label_id'] = new_label_id;
			labels[label_index]['label'] = label;
			labels[label_index]['tag_color'] = new_tag_color;
			
			delete labels[label_index]['new'];
			
			setLabels([...labels]);
		} catch(e)
		{
			console.log(e.response);
		} finally
		{
			setLoadingLabelIDs((previous_ids) => previous_ids.filter((temp_id) => `${temp_id}` !== `${label_id}`));
		}
	}

	const updateLabelColor = async (label_id, tag_color) => {
		const label_index = labels.findIndex(label => label.label_id === label_id);
		if(label_index === -1) return;

		const label_info = labels[label_index];
		const previous_tag_color = getLabelColorClass(label_info);
		if(previous_tag_color === tag_color) return;

		labels[label_index].tag_color = tag_color;
		setLabels([...labels]);

		if(label_info.new) return;

		const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, update_type:'update_color', label_id:label_id, label:label_info.label, tag_color:tag_color};
		const apiName = process.env.REACT_APP_ENDPOINT_NAME;
		const url = '/admin/update-label';
		const myInit = {
			response: true,
			body: JSON.stringify(data),
		};

		try {
			const response = await API.post(apiName, url, myInit);
			const saved_tag_color = (response.data && response.data.tag_color) ? response.data.tag_color : tag_color;
			labels[label_index].tag_color = saved_tag_color;
			setLabels([...labels]);
		} catch(e)
		{
			labels[label_index].tag_color = previous_tag_color;
			setLabels([...labels]);
			console.log(e.response);
		}
	}
	
	const removeLabel = async (label_id) => {
		const label_index = labels.findIndex(label => label.label_id === label_id);
		const label_info = labels[label_index];
		const is_new = ('new' in label_info) ? true : false;

		if(`${editingLabelID}` === `${label_id}`) cancelEditingLabelName();
		
		if(is_new)
		{
			labels.splice(label_index, 1);
			setLabels([...labels]);
		}
		else 
		{
			const data = {school_id:schoolInfo.school_id, schedule_version_id:schoolInfo.current_schedule_version_id, update_type:'remove', label_id:label_id, label:label_info.label};
			
			const options =  {
				title: "Are you sure?",
				text: `Do you really want to delete the "${label_info.label}" team/label? This will remove this team/label from all sections and students`,
				icon: "warning",
				dangerMode: true,
				buttons: {
					cancel: {
						text: "Cancel",
						value: false,
						visible: true,
						className: 'gray-btn'
					},
					confirm: {
						text: "Yes",
						value: true,
						visible: true,
						className: 'red-btn'
					},
				}
			}
			
			const delete_label = await swal(options);
			
			if(delete_label)
			{
				const apiName = process.env.REACT_APP_ENDPOINT_NAME;
				const url = '/admin/update-label';
			    const myInit = { // OPTIONAL
			        response: true,
			        body: JSON.stringify(data),
			    };
			   
				try {
					setLoadingLabelIDs((previous_ids) => previous_ids.includes(`${label_id}`) ? previous_ids : [...previous_ids, `${label_id}`]);
					await API.post(apiName, url, myInit);
					
					//Find all sections that have this label and remove it
					const sections_with_this_label = sections.filter(section => section.labels.includes(label_id));
					
					sections_with_this_label.forEach(section_with_label_info => {
						const section_id = section_with_label_info.section_id;
						const section_index = sections.findIndex(section => section.section_id === section_id);
						const section_info = sections[section_index];
						
						const section_labels = section_info.labels;
						const label_index = section_labels.findIndex(label => label === label_id);
						
						section_info.labels.splice(label_index, 1);
					});
					
					setSections([...sections]);
					
					// Remove label from JSON
					labels.splice(label_index, 1);
					setLabels([...labels]);
				} catch(e)
				{
					console.log(e.response);
					return;
				} finally
				{
					setLoadingLabelIDs((previous_ids) => previous_ids.filter((temp_id) => `${temp_id}` !== `${label_id}`));
				}
			}	
		}
	}
	
	useEffect(() => {
		if(!teachersLoading && !coursesLoading && !sectionsLoading) setIsLoading(false);
	}, [teachersLoading, coursesLoading, sectionsLoading, setIsLoading]);

	useEffect(() => {
		if(!userInfo || subscreenRestricted || subscreenLocked) return;

		setScreenAllowsUniversalSidebar(true);
		setUniversalSidebarLoadingInfo({sidebar_name:"Create Labels Notes", sidebar_type:'notes', subscreen:'create_labels', department_id:null, user_id:userInfo.user_id, help_link_path:`/app/${schoolType}/documentation/scheduling_wizard/create_sections/create_labels`});
	}, [userInfo, subscreenRestricted, subscreenLocked, setScreenAllowsUniversalSidebar, setUniversalSidebarLoadingInfo]);
	
	const sorted_labels = sortArrayOfObjects(labels, 'label', 'text', 'asc');
	
	const current_user_is_admin = (userInfo.role_id === '1'|| userInfo.role_id === '2' || userInfo.role_id === '5');
	
	return (
		<>
		<h1 className='progress-main-header'>Create Teams/Labels</h1>
		<p className='progress-subheader' style={{width:'450px'}}>You can create groups/teams that aren't covered by course codes.</p>
		{(subscreenRestricted) ?
		(
			<div className='general-screen-message'>
				<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
				<h1>No Access</h1>
				<div>You do not have access to this screen</div>	
			</div>
		):(subscreenLocked) ?
		(
			<div className='general-screen-message'>
				<FontAwesomeIcon className='dark-blue-text' style={{fontSize:'60px'}} icon={faLock}/>
				<h1>Temporarily Locked</h1>
				<div>This screen is locked until all other prior steps are completed</div>	
			</div>
		):(current_user_is_admin) ?
			(
				<div id='create-labels-dashboard'>
					<div className='create-labels-label-actions-row'>
						<div className='create-labels-help-toggle' onClick={() => setShowLabelsDescription(!showLabelsDescription)}>
							<FontAwesomeIcon className='blue-link' icon={faQuestionCircle}/>
							<div className='blue-link'>What are teams/labels?</div>
						</div>
						<div className='blue-link' onClick={addLabel}>Add Team/Label</div>
					</div>
					{showLabelsDescription && <p className='medium-text create-labels-description'>Create teams/labels to group students and tag sections that can only allow certain students.</p>}
					{sorted_labels.map((label, index) => (
						<div key={label.label_id}>
							{loadingLabelIDs.includes(`${label.label_id}`) ?
								(
									<div className='create-labels-label create-labels-label-loading'>
										<img src={require('../../../images/balls.gif')} alt='loading...' style={{height:'28px'}}/>
									</div>
								):
								(
									<>
							{label.label && `${editingLabelID}` !== `${label.label_id}` ?
								(
									<div className='create-labels-label'>
										<LabelColorPicker
											colorClass={getLabelColorClass(label)}
											options={isInclusionLabel(label) ? [{value:'yellow', display:'Inclusion Yellow'}] : LABEL_COLOR_OPTIONS}
											disabled={isInclusionLabel(label)}
											onChange={(new_color) => updateLabelColor(label.label_id, new_color)}
										/>
										<div className='create-labels-editable-name' title='Click to edit' onClick={() => startEditingLabelName(label)}>{label.label}</div>
										<FontAwesomeIcon className='cancel-link' icon={faTimes} onClick={() => removeLabel(label.label_id)}/>
									</div>
								) :
								(
									<>
										{label.label ?
											(
												<div className='create-labels-editing-label'>
													<LabelColorPicker
														colorClass={getLabelColorClass(label)}
														options={isInclusionLabel(label) ? [{value:'yellow', display:'Inclusion Yellow'}] : LABEL_COLOR_OPTIONS}
														disabled={isInclusionLabel(label)}
														onChange={(new_color) => updateLabelColor(label.label_id, new_color)}
													/>
													<input
														className='create-labels-input'
														value={editingLabelName}
														placeholder='Update label name...'
														onChange={(e) => setEditingLabelName(e.target.value)}
														onKeyDown={(e) => {
															if(e.key === 'Enter') saveEditedLabelName(label.label_id);
															if(e.key === 'Escape') cancelEditingLabelName();
														}}
														autoFocus={true}
														disabled={isSavingEditedLabelName}
													/>
													<FontAwesomeIcon className='gray-to-red-link' icon={faTimes} onClick={cancelEditingLabelName}/>
													<FontAwesomeIcon className='gray-to-blue-link' icon={faCheck} onClick={() => saveEditedLabelName(label.label_id)}/>
												</div>
											):
											(
												<div className='create-labels-editing-label'>
													<LabelColorPicker
														colorClass={getLabelColorClass(label)}
														options={LABEL_COLOR_OPTIONS}
														onChange={(new_color) => updateLabelColor(label.label_id, new_color)}
													/>
													<input className='create-labels-input' placeholder='Add label name...' onKeyPress={(e) => {if(e.key === 'Enter') updateLabel(e, label.label_id)}} autoFocus={true}/>
													<FontAwesomeIcon className='gray-to-red-link' icon={faTimes} onClick={() => removeLabel(label.label_id)}/>
													<FontAwesomeIcon className='gray-to-blue-link' icon={faCheck} onClick={(e) => updateLabel(e, label.label_id)}/>
												</div>
											)
										}
									</>
								)
							}
									</>
								)
							}
						</div>
					))}
			</div>
		):(userInfo && !current_user_is_admin) ?
		(
			<div className='locked-screen-notification-container'>
				<FontAwesomeIcon className='blue-text' icon={faLock} style={{fontSize:'50px'}}/>
				<h1>Locked</h1>
				<p>This step is only accessible to the master scheduler. You should be notified when future steps are unlocked!</p>
				<Link to='/progress' className='blue-text'>Go Back</Link>
			</div>
		): null}
		{(!subscreenLocked && !subscreenRestricted && current_user_is_admin) &&
			<div className='fixed-bottom-save-container'>
				<Button classes={['btn', 'btn-extra-large', 'green-btn', 'align-right', 'move-to-next-screen-btn']} selected={false} text='Go to Next Step: Student Labels' onClickFunction={handleMoveToNextScreen} />
			</div>
		}
		</>
	);
}
